import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * A suburb's stored state: the region it is assigned to (null = unassigned), whether Picaluna covers it, and the
 * sub-regions of that region it is in (#260).
 */
export interface SuburbAreaRow {
  salCode: string;
  regionId: string | null;
  covered: boolean;
  subRegions?: string[];
}

/**
 * One edit from the suburb map. `from` is what the map showed, so a stale edit comes back as a conflict. A
 * 'subregion' edit joins ('1') or leaves ('0') the sub-region `subRegionId`; the server puts an unassigned suburb in
 * the sub-region's region first, and refuses one in another region.
 */
export interface SuburbAreaEdit {
  salCode: string;
  field: 'region' | 'covered' | 'subregion';
  from: string | null;
  to: string | null;
  subRegionId?: string;
}

export interface SuburbRegion {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  order: number | null;
}

export interface SuburbPageRef {
  salCode: string;
  regionId: string | null;
  recordId: string;
  name: string | null;
  slug: string | null;
  label: string;
}

export interface SuburbAreaEditResult {
  batchId?: string;
  summary: { assigned: number; reassigned: number; unassigned: number; covered: number; uncovered: number; unchanged: number; joined?: number; left?: number };
  conflicts: Array<SuburbAreaEdit & { actual: string | null }>;
  refused: Array<{ salCode: string; field: string; reason: string; page?: string; subRegionId?: string }>;
  rows: SuburbAreaRow[];
}

/** A sub-region (#260): a 'district' record in content.suburbs, inside one region. `members` = suburbs in it now. */
export interface SuburbSubRegion {
  id: string;
  name: string;
  slug: string;
  regionId: string;
  published: boolean;
  mapColour: number | null;
  inSentence: string | null;
  members: number;
}

/** One sub-region in full: its members (with their page, if any), its members' councils and its page's gate report. */
export interface SuburbSubRegionDetail extends Omit<SuburbSubRegion, 'members'> {
  region: { id: string; name: string; slug: string; state: string | null } | null;
  members: Array<{ salCode: string; name: string; state: string; page: string | null }>;
  councils: Array<{ lgaCode: string; lgaName: string | null; members: number }>;
  gates: any[];
}

/** An ABS council area (LGA 2021) for "Fill from council areas", counted against one sub-region. */
export interface SuburbCouncil {
  lgaCode: string;
  lgaName: string | null;
  state: string;
  suburbs: number;
  members: number;
  inRegion: number;
  unassigned: number;
  otherRegion: number;
}

export interface SuburbFillResult {
  dryRun: boolean;
  batchId?: string;
  summary: { joined: number; assigned: number; already: number; skipped: number };
  skipped: Array<{ salCode: string; name: string; regionId: string | null; reason?: string }>;
  rows: SuburbAreaRow[];
}

/**
 * Suburb areas (conductor-server #247, #256, #260): which region each ABS suburb is in, whether Picaluna covers it,
 * and which of that region's sub-regions it is in. Every edit is one batch on the server, which is also one undo step.
 */
@Injectable({ providedIn: 'root' })
export class SuburbAreasService {
  private readonly paths = environment.api.paths.api.suburbAreas;

  constructor(private http: HttpClient) {}

  private url(path: string): string {
    return environment.api.host + path;
  }

  meta(): Observable<{ regions: SuburbRegion[]; pages: SuburbPageRef[]; subRegions: SuburbSubRegion[] }> {
    return this.http.get<any>(this.url(this.paths.meta)).pipe(map((r) => r.data));
  }

  /** Every stored row, read a page (≤ 5,000) at a time. */
  async allRows(): Promise<SuburbAreaRow[]> {
    const out: SuburbAreaRow[] = [];
    for (let offset = 0; ; offset += 5000) {
      const page: any = (await this.http.get<any>(this.url(this.paths.list), { params: { offset: String(offset), limit: '5000' } }).toPromise()).data;
      out.push(...page.rows);
      if (out.length >= page.total || !page.rows.length) return out;
    }
  }

  edit(changes: SuburbAreaEdit[], confirmLoss = false): Observable<SuburbAreaEditResult> {
    return this.http.patch<any>(this.url(this.paths.patch), { changes, confirmLoss }).pipe(map((r) => r.data));
  }

  undo(batchId: string): Observable<{ conflicts: number; rows?: SuburbAreaRow[] }> {
    return this.http.post<any>(this.url(this.paths.undo), { batchId }).pipe(map((r) => r.data));
  }

  subRegion(id: string): Observable<SuburbSubRegionDetail> {
    return this.http.get<any>(this.url(this.paths.subRegion.replace(':id', encodeURIComponent(id)))).pipe(map((r) => r.data));
  }

  /** Councils for a sub-region's fill: without q, those holding a suburb of its region; with q, by name (≤ 50). */
  councilAreas(subRegionId: string, q = ''): Observable<{ lgaLoaded: boolean; councils: SuburbCouncil[] }> {
    const params: Record<string, string> = { subRegionId };
    if (q.trim()) params.q = q.trim();
    return this.http.get<any>(this.url(this.paths.councilAreas), { params }).pipe(map((r) => r.data));
  }

  /** Every suburb of these councils joins the sub-region (one batch, one undo step); dryRun previews it. */
  fill(body: { subRegionId: string; lgaCodes: string[]; assignUnassigned: boolean; dryRun?: boolean }): Observable<SuburbFillResult> {
    return this.http.post<any>(this.url(this.paths.fill), body).pipe(map((r) => r.data));
  }
}
